import React from 'react';
import isNil from 'lodash/isNil';
import { ErrorMessage } from '@atlaskit/form';
import FileIcon from '@atlaskit/icon/glyph/editor/file';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import MagicFile from '../../../assets/MagicFile.svg';
import type { UploadedFileName } from '../../../types';
import { FileCard } from '../file-card';
import messages from './messages';

type Props = {
	isFileUploading: boolean;
	isFileNotCSV: boolean;
	hasErrorOccurred: boolean;
	isFileTooLarge: boolean;
	uploadedFileName: UploadedFileName;
	onRemoveFile: () => void;
	isLeaner?: boolean;
};

export const DropzoneContent = ({
	isFileUploading,
	isFileNotCSV,
	hasErrorOccurred,
	isFileTooLarge,
	uploadedFileName,
	onRemoveFile,
	isLeaner,
}: Props) => {
	const { formatMessage } = useIntl();

	if (isFileUploading) {
		return (
			<Stack alignInline="center" alignBlock="center" grow="fill">
				<Spinner size="medium" />
			</Stack>
		);
	}

	if (!isNil(uploadedFileName)) {
		return (
			<Stack alignInline="center" alignBlock="center" grow="fill">
				<FileCard fileName={uploadedFileName} onRemoveFile={onRemoveFile} />
			</Stack>
		);
	}

	const renderErrorMessage = () => (
		<>
			{isFileNotCSV && (
				<ErrorMessage testId="project-create-importer-upload-flow.common.ui.file-dropzone.dropzone-content.error-message">
					{formatMessage(messages.unsupportedFileErrorMessage)}
				</ErrorMessage>
			)}
			{hasErrorOccurred && (
				<ErrorMessage>{formatMessage(messages.uploadingErrorMessage)}</ErrorMessage>
			)}
			{isFileTooLarge && (
				<ErrorMessage>{formatMessage(messages.fileSizeTooLargeErrorMessage)}</ErrorMessage>
			)}
		</>
	);

	if (fg('enable-x2c-standardised-flow')) {
		return (
			<Stack alignInline="center" alignBlock="center" grow="fill">
				<Flex xcss={imageStyles} alignItems="center">
					<Box as="img" src={MagicFile} alt="" width={isLeaner ? '60px' : '100px'} />
					<Box>
						<Box xcss={[textWrapperStyles, ...(isLeaner ? [textWrapperLeanerStyles] : [])]}>
							<Box as="span" xcss={headingLeanerStyles}>
								{formatMessage(messages.selectFileToUploadDescription)}
							</Box>
							<Box as="span" xcss={descriptionStyles}>
								{formatMessage(messages.dragAndDropDescription)}
							</Box>
						</Box>
						{renderErrorMessage()}
					</Box>
				</Flex>
			</Stack>
		);
	}

	return (
		<Stack alignInline="center" alignBlock="center" grow="fill">
			<Flex xcss={iconStyles} alignItems="center">
				<Box as="div" xcss={fileIconStyles}>
					<FileIcon
						label="file icon"
						size="xlarge"
						primaryColor={token('color.icon.subtle', colors.N40)}
					/>
				</Box>

				<Box xcss={textWrapperStyles}>
					<Box as="h4" xcss={headingStyles}>
						{formatMessage(messages.selectFileToUploadDescription)}
					</Box>
					<Box as="p" xcss={descriptionStyles}>
						{formatMessage(messages.dragAndDropDescription)}
					</Box>
				</Box>
			</Flex>
			{renderErrorMessage()}
		</Stack>
	);
};

const headingStyles = xcss({
	margin: 'space.0',
});

const headingLeanerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	font: token('font.heading.xsmall', fontFallback.heading.xsmall),
});

const iconStyles = xcss({
	gap: 'space.0',
	flexDirection: 'column',
});

const imageStyles = xcss({
	gap: 'space.200',
	flexDirection: 'row',
});

const fileIconStyles = xcss({
	alignSelf: 'center',
});

const descriptionStyles = xcss({
	margin: 'space.0',
	color: 'color.text.subtle',
});

const textWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'column',
});

const textWrapperLeanerStyles = xcss({
	flexDirection: 'row',
	gap: 'space.050',
});
